import { useEffect, useRef, useState } from 'react';
import type { LngLat } from '../../shared/protocol';

export interface GeoSample extends LngLat {
  acc: number;
  ts: number;
}

/** Latest fix from the active watch — used as the fresh-fix fallback. */
let lastFix: GeoSample | null = null;

/** When the local simulator is on, this overrides all GPS reads. */
let simOverride: GeoSample | null = null;
export function setSimPosition(pos: LngLat | null): void {
  simOverride = pos ? { lat: pos.lat, lng: pos.lng, acc: 5, ts: Date.now() } : null;
  if (simOverride) lastFix = simOverride;
}

/**
 * Force a fresh GPS read at the moment a card or Found is submitted — cached
 * reads drift 100–200 ft (§6.7 / §4.8.2). Prefers a fresh getCurrentPosition,
 * but if it doesn't return promptly (or errors) we fall back to the latest
 * watched position so a play is never lost.
 */
export function getFreshFix(timeoutMs = 5000): Promise<GeoSample> {
  if (simOverride) return Promise.resolve({ ...simOverride, ts: Date.now() });
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      return lastFix ? resolve(lastFix) : reject(new Error('no geolocation'));
    }
    let settled = false;
    const done = (s: GeoSample) => {
      if (settled) return;
      settled = true;
      resolve(s);
    };
    // Fall back to the most recent watched fix if a fresh read stalls.
    const fallback = setTimeout(() => {
      if (!settled && lastFix) done(lastFix);
    }, 1500);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        clearTimeout(fallback);
        done({ lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy, ts: Date.now() });
      },
      (e) => {
        clearTimeout(fallback);
        if (lastFix) done(lastFix);
        else if (!settled) {
          settled = true;
          reject(new Error(e.message || 'gps error'));
        }
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 },
    );
  });
}

/**
 * Continuous watch. Calls `onSample` on every fix; also returns the latest for
 * rendering "GPS locked". Enabled gating lets us only stream once teamed.
 */
export function useGeoWatch(enabled: boolean, onSample?: (s: GeoSample) => void) {
  const [sample, setSample] = useState<GeoSample | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cb = useRef(onSample);
  cb.current = onSample;

  useEffect(() => {
    if (!enabled || !('geolocation' in navigator)) return;
    const id = navigator.geolocation.watchPosition(
      (p) => {
        const s: GeoSample = {
          lat: p.coords.latitude,
          lng: p.coords.longitude,
          acc: p.coords.accuracy,
          ts: Date.now(),
        };
        lastFix = s;
        setSample(s);
        setError(null);
        cb.current?.(s);
      },
      (e) => setError(e.message || 'gps error'),
      { enableHighAccuracy: true, maximumAge: 2000, timeout: 10000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [enabled]);

  return { sample, error };
}
